import { RefObject, useEffect, useState } from "react";

/**
 * Measures the vertical center of a carousel slide's image so the navigation
 * arrows can sit on the image instead of the full card. These carousels stack an
 * image on top with a title / metadata below it, so centering on the whole card
 * pushes the arrows below the image. This returns the image's vertical center
 * (relative to `containerRef`) for use as the arrows' `top`.
 *
 * Locating the image generically across card types:
 * - Slides are sometimes wrapped in sizing `<div>`s (e.g. `basis-[320px]`), so we
 *   descend through any wrapper that holds a single in-flow child until we reach
 *   the card body — the element that stacks the image and the text (i.e. has more
 *   than one in-flow child). Its first in-flow child is the image box.
 * - Absolutely-positioned children (badges, hide buttons, hover overlays,
 *   playback-progress bars) are out of flow and skipped, so they never get
 *   mistaken for the image.
 *
 * Re-measures on container resize (covers breakpoint changes) and whenever `deps`
 * change (e.g. the slides array).
 *
 * @param containerRef positioned ancestor the arrows are absolutely placed within
 * @param trackRef the flex row that directly contains the slide elements
 * @param deps re-measure when these change
 * @returns the arrow `top` in px relative to `containerRef`, or null until measured
 */
export function useImageCenteredArrowOffset(
  containerRef: RefObject<HTMLElement | null>,
  trackRef: RefObject<HTMLElement | null>,
  deps: unknown[]
): number | null {
  const [arrowTop, setArrowTop] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track) return;

    const inFlowChildren = (el: Element) =>
      Array.from(el.children).filter(
        (child) => getComputedStyle(child).position !== "absolute"
      );

    const measure = () => {
      const firstSlide = track.firstElementChild;
      if (!firstSlide) return;

      // Descend through single-child wrappers to the card body, then take its
      // first in-flow child as the image box.
      let node: Element = firstSlide;
      let children = inFlowChildren(node);
      while (children.length === 1) {
        node = children[0];
        children = inFlowChildren(node);
      }

      const image = children[0];
      if (!image) return;

      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      setArrowTop(imageRect.top - containerRect.top + imageRect.height / 2);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return arrowTop;
}
